import React from "react";
import { useTranslation } from "react-i18next";
import { getLocalizedTitle } from "../../../../utils/localizedText";
import { parseQuotationFee, parseQuotationPerTime, calculateServiceItemTotal } from "../../../../utils/quotationPricing";
import "./QuotationDetails.css";

const QuotationTable = ({ items, onUnitFeeChange, onTransportationFeeChange, onAdjustmentChange, onPerTimeChange }) => {
  const { t, i18n } = useTranslation();

  const parseMoney = parseQuotationFee;

  // Per Time is a positive-integer multiplier; blank/invalid/zero falls back to the default of 1.
  const getPerTime = parseQuotationPerTime;

  // Comma-format a fee value for display in the (text) input while typing.
  const formatFeeInput = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const num = Number(String(value).replace(/,/g, ""));
    return Number.isFinite(num) ? num.toLocaleString("en-US") : value;
  };

  // Strip commas/invalid characters before storing, so the underlying value stays a plain numeric string.
  const makeCommaFieldHandler = (onChange) => (index, rawInput) => {
    const cleaned = rawInput.replace(/,/g, "");
    if (cleaned !== "" && !/^\d*\.?\d*$/.test(cleaned)) return;
    onChange(index, cleaned);
  };

  const handleUnitFeeInputChange = makeCommaFieldHandler(onUnitFeeChange);
  const handleTransportationFeeInputChange = makeCommaFieldHandler(onTransportationFeeChange);
  const handleAdjustmentInputChange = makeCommaFieldHandler(onAdjustmentChange);

  const handlePerTimeInputChange = (index, rawInput) => {
    if (rawInput !== "" && !/^\d*$/.test(rawInput)) return;
    onPerTimeChange(index, rawInput);
  };

  // Total per service = (serviceFee × per_time) + transportFee + additionalFee
  const calculateItemTotal = (item) =>
    calculateServiceItemTotal({ serviceFee: item.unitFee, transportFee: item.transportationFee, additionalFee: item.adjustment, perTime: item.perTime });

  const calculateTotal = () => items.reduce((sum, item) => sum + calculateItemTotal(item), 0);

  const formatCurrency = (amount) => {
    const n = Math.round(parseMoney(amount));
    return `₫${n.toLocaleString("en-US")}`;
  };

  return (
    <div className="quotation-table-container">
      <table className="quotation-table">
        <thead className="quotation-thead">
          <tr>
            <th>{t("serialNo")}</th>
            <th>{t("serviceName")}</th>
            <th>{t("perTime") || "Per Time"}</th>
            <th>{t("distanceKm") || "Distance(KM)"}</th>
            <th>{t("serviceFee") || "Service Fee"}</th>
            <th>{t("transportationFee")}</th>
            <th>{t("adjustment")}</th>
            <th>{t("subtotal")}</th>
          </tr>
        </thead>
        <tbody className="quotation-tbody">
          {items.map((item, index) => {
            const unitFee = parseMoney(item.unitFee);
            const perTime = getPerTime(item.perTime);
            const transportationFee = parseMoney(item.transportationFee);
            const adjustment = parseMoney(item.adjustment);
            const subtotal = calculateItemTotal(item);

            return (
              <tr key={index} className="quotation-row">
                <td>{index + 1}</td>
                <td>
                  {getLocalizedTitle(item.serviceItemData || item.serviceItemTitle, i18n.language) || item.serviceName}
                  {(item.dateRange || item.startTime || item.endTime) && (
                    <div style={{ fontSize: "1.2rem", color: "#000", marginTop: "0.3rem", whiteSpace: "nowrap" }}>
                      {item.dateRange ? <span>{item.dateRange}</span> : null}
                      {(item.startTime || item.endTime) && (
                        <span style={{ display: "block" }}>
                          {item.startTime}{item.startTime && item.endTime ? " → " : ""}{item.endTime}
                        </span>
                      )}
                    </div>
                  )}
                </td>
                <td>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={item.perTime ?? 1}
                    onChange={(e) => handlePerTimeInputChange(index, e.target.value)}
                    className="adjustment-input per-time-input"
                  />
                </td>
                <td>{item.distanceInKm !== "" && item.distanceInKm !== null && item.distanceInKm !== undefined ? item.distanceInKm : "-"}</td>
                <td>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formatFeeInput(item.unitFee)}
                    onChange={(e) => handleUnitFeeInputChange(index, e.target.value)}
                    placeholder="Vnd 0"
                    className="adjustment-input"
                  />
                  {perTime !== 1 && <div className="fee-computed-hint">= {formatFeeInput(unitFee * perTime)}</div>}
                </td>
                <td>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formatFeeInput(item.transportationFee)}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => handleTransportationFeeInputChange(index, e.target.value)}
                    placeholder="Vnd 0"
                    className="adjustment-input"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formatFeeInput(item.adjustment)}
                    onChange={(e) => handleAdjustmentInputChange(index, e.target.value)}
                    placeholder="Vnd 0"
                    className="adjustment-input"
                  />
                </td>
                <td className="subtotal-cell">{formatCurrency(subtotal)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot className="quotation-tfoot">
          <tr className="total-row">
            <td colSpan={7} className="total-label">
              <strong>{t("total")}</strong>
            </td>
            <td className="total-value">
              <strong>{formatCurrency(calculateTotal())}</strong>
            </td>
          </tr>
        </tfoot>
      </table>
      <p className="quotation-disclaimer">{t("quotationDisclaimer") || "Please note that actual prices may vary after the service is provided. The figures mentioned are just estimations."}</p>
    </div>
  );
};

export default QuotationTable;
